import { APIService, FetchOptions } from "./APIService";
import { LoggerService } from "../Logger/LoggerService";
import { ZodSchema } from "zod";
import { APIError, ValidationError } from "./Error";

export class HttpAPIService implements APIService {
  #baseUrl: string;
  #logger: LoggerService;
  #getToken?: () => string | null;

  constructor(
    baseUrl: string,
    logger: LoggerService,
    getToken?: () => string | null,
  ) {
    this.#baseUrl = baseUrl;
    this.#logger = logger;
    this.#getToken = getToken;
  }

  async request<T>(options: FetchOptions, schema?: ZodSchema<T>): Promise<T> {
    const { path, method = "GET", query, body } = options;
    const url = new URL(`${this.#baseUrl}${path}`);
    if (query) {
      Object.entries(query).forEach(([key, value]) =>
        url.searchParams.append(key, value),
      );
    }
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.#getToken) {
      const token = this.#getToken();
      if (token) headers["X-API-key"] = token;
    }
    const response = await fetch(url.toString(), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new APIError(
        `HTTP error ${response.status}`,
        response.status,
        data,
      );
      this.#logger.captureException(error, {
        url: url.toString(),
        method,
        requestBody: body,
        responseData: data,
      });
      throw error;
    }
    if (!schema) {
      return data as T;
    }
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      const error = new ValidationError(
        "Invalid response format",
        parsed.error,
        data,
      );
      this.#logger.captureException(error, {
        url: url.toString(),
        method,
        requestBody: body,
        responseData: data,
      });
      throw error;
    }
    return parsed.data;
  }
}
